import { useState, useEffect, ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import api, { getOffer, signOffer } from "../services/api";
import { formatPrice } from "../lib/utils";
import { differenceInDays, differenceInHours, format, isBefore } from "date-fns";
import { nl } from "date-fns/locale";
import axios from "axios";
import PageShell from "../components/layout/PageShell";
import { OrderTicket } from "../components/OrderTicket";

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
}

interface Submission {
  id: string;
  formula: string;
  volwassenen: number;
  kinderen: number;
  vegetarisch: number;
  eventLocatieNaam: string;
}

type OfferStatus = "DRAFT" | "PENDING" | "ACCEPTED" | "REJECTED";

interface Offer {
  id: string;
  status: OfferStatus;
  firstName: string;
  lastName: string;
  email: string;
  eventDate: string;
  eventStartTime: string;
  eventEndTime: string;
  companyName?: string;
  vatNumber?: string;
  billingStreet: string;
  billingNumber: string;
  billingZip: string;
  billingCity: string;
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
  notes?: string;
  terms?: string;
  personalMessage?: string;
  lineItems: LineItem[];
  signedBy?: string;
  signedAt?: string;
  confirmedAt?: string | null;
  formula?: string | null;
  submission?: Submission;
  archived: boolean;
  dueDate: string;
}

interface RelatedOffer {
  publicId: string;
  status: OfferStatus;
  eventDate: string;
  issueDate: string;
  dueDate: string;
  totalAmount: number;
  confirmedAt: string | null;
  formula: string | null;
  location: string | null;
}

const longDate = (value: string) => format(new Date(value), "EEEE d MMMM yyyy", { locale: nl });

const statusLabel = (offer: { status: OfferStatus; confirmedAt?: string | null; dueDate: string }) => {
  if (offer.status === "ACCEPTED") return offer.confirmedAt ? "Bevestigd" : "Ondertekend";
  if (offer.status === "REJECTED") return "Geweigerd";
  if (isBefore(new Date(offer.dueDate), new Date())) return "Verlopen";
  return "Te ondertekenen";
};

const getRemainingTime = (dueDate: string) => {
  const now = new Date();
  const due = new Date(dueDate);
  const days = differenceInDays(due, now);
  const hours = differenceInHours(due, now) % 24;
  if (days > 0) return `Nog ${days} ${days === 1 ? "dag" : "dagen"} geldig`;
  if (hours > 0) return `Nog ${hours}u geldig`;
  return "Verlopen";
};

const isEventWithin5Days = (eventDate: string) => {
  const diffDays = Math.ceil((new Date(eventDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diffDays <= 5 && diffDays >= 0;
};

export default function PublicOffer() {
  const { publicId } = useParams();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [related, setRelated] = useState<RelatedOffer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  useEffect(() => {
    if (!publicId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getOffer(publicId)
      .then((data) => {
        if (!cancelled) setOffer(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(axios.isAxiosError(err) && err.response?.status === 404 ? null : "Je offerte kon niet geladen worden.");
        setOffer(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    // Other offers for the same customer, e.g. one per formula
    api
      .get(`/offers/${publicId}/related`)
      .then((response) => {
        if (!cancelled) setRelated(response.data.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setRelated([]);
      });
    return () => {
      cancelled = true;
    };
  }, [publicId]);

  const handleSign = async (action: "ACCEPTED" | "REJECTED") => {
    if (!publicId) return;
    if (!name.trim()) {
      setSignError("Vul je volledige naam in om de offerte te ondertekenen.");
      return;
    }
    setIsSubmitting(true);
    setSignError(null);
    try {
      await signOffer(publicId, name.trim(), action);
      setOffer(await getOffer(publicId));
      api.get(`/offers/${publicId}/related`).then((response) => setRelated(response.data.data ?? [])).catch(() => undefined);
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : null;
      setSignError(message || "Er ging iets mis bij het ondertekenen. Probeer het later opnieuw.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageShell>
        <StateMessage eyebrow="Jouw offerte" title="Je offerte wordt geladen…" />
      </PageShell>
    );
  }

  if (error || !offer || (offer.archived && offer.status !== "ACCEPTED")) {
    return (
      <PageShell>
        <StateMessage
          eyebrow="Offerte niet gevonden"
          title={error ?? "We vinden deze offerte niet"}
          text="Controleer of je de volledige link uit je mail gebruikt. Lukt het niet? Mail ons en we sturen je offerte opnieuw."
        />
      </PageShell>
    );
  }

  if (offer.status === "DRAFT") {
    return (
      <PageShell>
        <StateMessage
          eyebrow="Even geduld"
          title="Je offerte is nog niet klaar"
          text="We leggen de laatste hand aan je offerte. Zodra ze klaar is, krijg je een mail met de link."
        />
      </PageShell>
    );
  }

  const isExpired = isBefore(new Date(offer.dueDate), new Date());
  const current = related.find((item) => item.publicId === publicId);
  const location = offer.submission?.eventLocatieNaam ?? current?.location ?? null;
  const formula = offer.formula ?? offer.submission?.formula ?? current?.formula ?? null;

  const vatByRate = (offer.lineItems || []).reduce((acc, item) => {
    acc[item.vatRate] = (acc[item.vatRate] ?? 0) + item.vatAmount;
    return acc;
  }, {} as Record<number, number>);

  const meta = [
    formula ? { label: "Formule", value: formula } : null,
    { label: "Datum", value: format(new Date(offer.eventDate), "d MMM yyyy", { locale: nl }) },
    { label: "Uren", value: `${offer.eventStartTime} - ${offer.eventEndTime}` },
    location ? { label: "Locatie", value: location } : null,
  ].filter((item): item is { label: string; value: string } => item !== null);

  const stamp =
    offer.status === "ACCEPTED"
      ? offer.confirmedAt
        ? { text: "Bevestigd", tone: "green" as const }
        : { text: "Ondertekend", tone: "brown" as const }
      : offer.status === "REJECTED"
        ? { text: "Geweigerd", tone: "red" as const }
        : undefined;

  return (
    <PageShell>
      {/* Header band */}
      <section className="relative overflow-hidden bg-bl-black text-white">
        <div className="plus-pattern absolute inset-0 opacity-[0.04]" aria-hidden="true" />
        <div className="page-container relative py-10 md:py-14">
          <p className="hero-rise eyebrow">Jouw offerte</p>
          <h1
            className="hero-rise mt-4 font-display text-4xl font-black tracking-tight md:text-5xl"
            style={{ "--rise-delay": "80ms" } as React.CSSProperties}
          >
            Offerte voor {offer.firstName}
          </h1>
          <p className="hero-rise mt-3 text-white/70" style={{ "--rise-delay": "160ms" } as React.CSSProperties}>
            {longDate(offer.eventDate)}
            {location ? ` · ${location}` : ""}
          </p>
        </div>
      </section>

      <section className="bg-bl-cream pb-20 pt-8">
        <div className="page-container">
          <StatusTimeline offer={offer} isExpired={isExpired} />

          <div className="mt-8 grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="space-y-6">
              {offer.status === "ACCEPTED" &&
                (offer.confirmedAt ? (
                  <Banner tone="green">
                    <p className="font-display text-lg font-bold">
                      Je boeking is bevestigd op {format(new Date(offer.confirmedAt), "d MMMM yyyy", { locale: nl })}
                    </p>
                    <p className="text-sm">Je vindt alle details ook in de bevestigingsmail.</p>
                  </Banner>
                ) : (
                  <Banner tone="brown">
                    <p className="font-display text-lg font-bold">Ondertekend, bedankt!</p>
                    <p className="text-sm">We bevestigen je boeking zo snel mogelijk.</p>
                  </Banner>
                ))}

              {offer.status === "ACCEPTED" && isEventWithin5Days(offer.eventDate) && (
                <Banner tone="gold">
                  <p className="font-bold">Klopt alles nog?</p>
                  <p className="text-sm">
                    Wijzigt er nog iets (aantal gasten, uren,…)? Laat het ons zo snel mogelijk weten via{" "}
                    <a href="mailto:ontour@burgerlijk.com" className="font-medium underline">
                      ontour@burgerlijk.com
                    </a>
                    .
                  </p>
                </Banner>
              )}

              {related.length > 1 && <RelatedOffers offers={related} currentId={publicId} />}

              {offer.personalMessage && (
                <Card>
                  <p className="whitespace-pre-line border-l-4 border-bl-gold pl-4 text-bl-black/80">{offer.personalMessage}</p>
                </Card>
              )}

              <Card title="Je event">
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Detail label="Datum" value={longDate(offer.eventDate)} />
                  <Detail label="Uren" value={`${offer.eventStartTime} - ${offer.eventEndTime}`} />
                  {location && <Detail label="Locatie" value={location} />}
                  {formula && <Detail label="Formule" value={formula} />}
                  {offer.submission && (
                    <Detail
                      label="Gasten"
                      value={`${offer.submission.volwassenen} volwassenen, ${offer.submission.kinderen} kinderen${
                        offer.submission.vegetarisch ? `, waarvan ${offer.submission.vegetarisch} vegetarisch` : ""
                      }`}
                    />
                  )}
                </dl>
              </Card>

              <Card title="Gegevens">
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Detail label="Naam" value={`${offer.firstName} ${offer.lastName}`} />
                  <Detail label="E-mail" value={offer.email} />
                  {offer.companyName && <Detail label="Bedrijf" value={offer.companyName} />}
                  <Detail
                    label="Facturatieadres"
                    value={`${offer.billingStreet} ${offer.billingNumber}, ${offer.billingZip} ${offer.billingCity}`}
                  />
                  {offer.vatNumber && <Detail label="Btw-nummer" value={offer.vatNumber} />}
                </dl>
              </Card>

              <Card title="Wat je bestelt">
                {/* Desktop table */}
                <div className="hidden overflow-hidden rounded border border-bl-black/10 md:block">
                  <table className="min-w-full text-sm">
                    <thead className="bg-bl-beige text-left text-xs uppercase tracking-wider text-bl-black/65">
                      <tr>
                        <th className="px-4 py-3 font-bold">Omschrijving</th>
                        <th className="px-4 py-3 text-right font-bold">Aantal</th>
                        <th className="px-4 py-3 text-right font-bold">Prijs</th>
                        <th className="px-4 py-3 text-right font-bold">Btw</th>
                        <th className="px-4 py-3 text-right font-bold">Totaal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bl-black/10">
                      {(offer.lineItems || []).map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">{item.description}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{item.quantity}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{formatPrice(item.unitPrice)}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{item.vatRate}%</td>
                          <td className="px-4 py-3 text-right font-medium tabular-nums">{formatPrice(item.totalAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-bl-black/10 bg-bl-cream/60">
                      <TotalRow label="Subtotaal (excl. btw)" value={formatPrice(offer.subtotal)} />
                      {Object.entries(vatByRate).map(([rate, amount]) => (
                        <TotalRow key={rate} label={`Btw ${rate}%`} value={formatPrice(amount)} />
                      ))}
                      <tr className="bg-bl-black text-white">
                        <td colSpan={4} className="px-4 py-3 text-right font-display text-sm font-bold uppercase tracking-wider">
                          Totaal incl. btw
                        </td>
                        <td className="px-4 py-3 text-right font-display text-lg font-black tabular-nums">{formatPrice(offer.totalAmount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="space-y-3 md:hidden">
                  {(offer.lineItems || []).map((item, index) => (
                    <div key={index} className="rounded border border-bl-black/10 p-4 text-sm">
                      <p className="font-medium">{item.description}</p>
                      <div className="mt-2 flex justify-between text-bl-black/65">
                        <span>
                          {item.quantity} × {formatPrice(item.unitPrice)} · btw {item.vatRate}%
                        </span>
                        <span className="font-medium text-bl-black">{formatPrice(item.totalAmount)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="space-y-1.5 rounded bg-bl-cream/70 p-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-bl-black/65">Subtotaal (excl. btw)</span>
                      <span>{formatPrice(offer.subtotal)}</span>
                    </div>
                    {Object.entries(vatByRate).map(([rate, amount]) => (
                      <div key={rate} className="flex justify-between">
                        <span className="text-bl-black/65">Btw {rate}%</span>
                        <span>{formatPrice(amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-bl-black/10 pt-2 font-display text-lg font-black">
                      <span>Totaal incl. btw</span>
                      <span>{formatPrice(offer.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {offer.terms && (
                <Card title="Voorwaarden">
                  <p className="whitespace-pre-line text-sm text-bl-black/75">{offer.terms}</p>
                </Card>
              )}
              {offer.notes && (
                <Card title="Opmerkingen">
                  <p className="whitespace-pre-line text-sm text-bl-black/75">{offer.notes}</p>
                </Card>
              )}

              {offer.status === "PENDING" && (
                <Card title="Offerte ondertekenen">
                  {isExpired ? (
                    <p className="text-sm text-bl-red">
                      Deze offerte is verlopen. Mail ons op{" "}
                      <a href="mailto:ontour@burgerlijk.com" className="font-medium underline">
                        ontour@burgerlijk.com
                      </a>{" "}
                      voor een nieuwe offerte.
                    </p>
                  ) : (
                    <form
                      className="space-y-4"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSign("ACCEPTED");
                      }}
                    >
                      <p className="text-sm text-bl-black/65">
                        Met je naam onderteken je deze offerte. {getRemainingTime(offer.dueDate)}.
                      </p>
                      <div>
                        <label htmlFor="signName" className="field-label">
                          Je volledige naam
                        </label>
                        <input
                          id="signName"
                          type="text"
                          autoComplete="name"
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            setSignError(null);
                          }}
                          aria-invalid={!!signError}
                          aria-describedby={signError ? "sign-error" : undefined}
                          className={`field ${signError ? "field-error" : ""}`}
                          placeholder={`${offer.firstName} ${offer.lastName}`}
                          disabled={isSubmitting}
                        />
                        {signError && (
                          <p id="sign-error" className="field-message" role="alert">
                            {signError}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <button type="button" onClick={() => handleSign("REJECTED")} disabled={isSubmitting} className="btn-ghost">
                          Weigeren
                        </button>
                        <button type="submit" disabled={isSubmitting} className="btn-red">
                          {isSubmitting ? "Bezig…" : "Onderteken offerte"}
                        </button>
                      </div>
                    </form>
                  )}
                </Card>
              )}

            </div>

            {/* On phones the ticket only earns its place once it carries a stamp */}
            <aside className={`${stamp ? "" : "hidden lg:block"} lg:sticky lg:top-6`}>
              <OrderTicket
                title="Jouw offerte"
                meta={meta}
                lines={(offer.lineItems || []).map((item, index) => ({
                  key: String(index),
                  label: item.description,
                  detail: `${item.quantity} × ${formatPrice(item.unitPrice)}`,
                  amount: formatPrice(item.totalAmount),
                }))}
                total={{ label: "Totaal incl. btw", value: formatPrice(offer.totalAmount) }}
                note={
                  offer.signedBy && offer.signedAt
                    ? `Ondertekend door ${offer.signedBy} op ${format(new Date(offer.signedAt), "d MMMM yyyy", { locale: nl })}.`
                    : offer.status === "PENDING"
                      ? `${getRemainingTime(offer.dueDate)}. Vragen? ontour@burgerlijk.com`
                      : undefined
                }
                stamp={stamp}
              />
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function StatusTimeline({ offer, isExpired }: { offer: Offer; isExpired: boolean }) {
  const steps = [
    { label: "Verstuurd", done: true },
    { label: offer.status === "REJECTED" ? "Geweigerd" : offer.status === "ACCEPTED" ? "Ondertekend" : "Te ondertekenen", done: offer.status === "ACCEPTED" || offer.status === "REJECTED" },
    { label: "Bevestigd", done: !!offer.confirmedAt },
  ];
  const currentIndex = steps.findIndex((step) => !step.done);
  const rejected = offer.status === "REJECTED";
  const expired = offer.status === "PENDING" && isExpired;

  return (
    <div>
      <ol className="flex gap-1.5" aria-label="Status van je offerte">
        {steps.map((step, index) => {
          const current = index === currentIndex;
          const barColor = rejected && index === 1 ? "bg-bl-red" : step.done ? "bg-bl-brown" : current ? "bg-bl-gold" : "bg-bl-black/10";
          return (
            <li key={step.label} className="flex-1" aria-current={current ? "step" : undefined}>
              <span className={`block h-1.5 rounded-full ${barColor}`} />
              <span
                className={`mt-2 block text-xs font-bold uppercase tracking-wider ${
                  rejected && index === 1 ? "text-bl-red" : step.done ? "text-bl-brown" : current ? "text-bl-black" : "text-bl-black/40"
                }`}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
      {expired && <p className="mt-3 text-sm font-medium text-bl-red">Deze offerte is verlopen en kan niet meer ondertekend worden.</p>}
    </div>
  );
}

function RelatedOffers({ offers, currentId }: { offers: RelatedOffer[]; currentId?: string }) {
  return (
    <Card title="Al je offertes bij Burgerlijk">
      <p className="-mt-2 mb-4 text-sm text-bl-black/65">
        We maakten meerdere offertes voor je. Kies de formule die je wil en onderteken die offerte.
      </p>
      <ul className="grid gap-3 sm:grid-cols-2">
        {offers.map((item) => {
          const isCurrent = item.publicId === currentId;
          const label = statusLabel(item);
          return (
            <li key={item.publicId}>
              <Link
                to={`/offers/${item.publicId}`}
                aria-current={isCurrent ? "page" : undefined}
                className={`block h-full rounded border p-4 transition-all duration-300 ${
                  isCurrent ? "border-bl-brown bg-bl-cream" : "border-bl-black/10 hover:-translate-y-0.5 hover:border-bl-brown hover:shadow-md"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-lg font-bold">{item.formula ?? "Offerte"}</p>
                  {isCurrent && (
                    <span className="whitespace-nowrap rounded-full bg-bl-brown px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
                      Deze offerte
                    </span>
                  )}
                </div>
                <p className="text-sm text-bl-black/65">{format(new Date(item.eventDate), "d MMMM yyyy", { locale: nl })}</p>
                <div className="mt-3 flex items-baseline justify-between">
                  <span
                    className={`text-xs font-bold uppercase tracking-wider ${
                      label === "Bevestigd" ? "text-emerald-700" : label === "Geweigerd" || label === "Verlopen" ? "text-bl-red" : "text-bl-brown"
                    }`}
                  >
                    {label}
                  </span>
                  <span className="font-display text-lg font-black tabular-nums">{formatPrice(item.totalAmount)}</span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm ring-1 ring-bl-black/5 sm:p-7">
      {title && <h2 className="mb-4 font-display text-lg font-bold">{title}</h2>}
      {children}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-bl-black/50">{label}</dt>
      <dd className="mt-0.5 font-medium">{value}</dd>
    </div>
  );
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td colSpan={4} className="px-4 py-2 text-right text-bl-black/65">
        {label}
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{value}</td>
    </tr>
  );
}

function Banner({ tone, children }: { tone: "green" | "brown" | "gold"; children: ReactNode }) {
  const tones = {
    green: "bg-emerald-700 text-white",
    brown: "bg-bl-brown text-white",
    gold: "bg-bl-gold/25 text-bl-black border-l-4 border-bl-gold",
  };
  return (
    <div className={`step-in rounded-lg px-5 py-4 ${tones[tone]}`} role="status">
      {children}
    </div>
  );
}

function StateMessage({ eyebrow, title, text }: { eyebrow: string; title: string; text?: string }) {
  return (
    <section className="bg-bl-cream py-16 md:py-24">
      <div className="page-container grid items-center gap-10 lg:grid-cols-2">
        <div className="space-y-5">
          <p className="hero-rise eyebrow">{eyebrow}</p>
          <h1 className="hero-rise font-display text-4xl font-black tracking-tight md:text-5xl" style={{ "--rise-delay": "80ms" } as React.CSSProperties}>
            {title}
          </h1>
          {text && (
            <p className="hero-rise max-w-xl text-bl-black/70" style={{ "--rise-delay": "160ms" } as React.CSSProperties}>
              {text}
            </p>
          )}
          {text && (
            <div className="hero-rise flex flex-wrap gap-3" style={{ "--rise-delay": "240ms" } as React.CSSProperties}>
              <Link to="/" className="btn-brown">
                Naar On Tour
              </Link>
              <a href="mailto:ontour@burgerlijk.com" className="btn-outline">
                Mail ons
              </a>
            </div>
          )}
        </div>
        <img src="/images/caravan-illustration.jpg" alt="" className="mx-auto w-full max-w-md mix-blend-multiply" />
      </div>
    </section>
  );
}
